import React from "react";
import {
  Control,
  UseFormSetValue,
  useWatch,
  useFormState,
} from "react-hook-form";
import { CampaignForm } from "@/partial/campaign/types";
import { SelectOptionType } from "@/components";
import { Flex, IconAddStyle } from "../channel/styles";
import { Button, IconDelete } from "tvs-unified-lib/dist";
import OperatorSelect from "@/components/OperatorSelect";
import SimpleInput from "@/components/SimpleInput";
import SimpleDatePicker from "@/components/SimpleDatePicker";

export const OPERATOR_OPTIONS: SelectOptionType[] = [
  { label: "EQUALS", value: "EQUALS" },
  { label: "NOT_EQUALS", value: "NOT_EQUALS" },
  { label: "BEGINS_WITH", value: "BEGINS_WITH" },
  { label: "ENDS_WITH", value: "ENDS_WITH" },
  { label: "CONTAINS", value: "CONTAINS" },
  { label: "BEFORE", value: "BEFORE" },
  { label: "AFTER", value: "AFTER" },
  { label: "BETWEEN", value: "BETWEEN" },
  { label: "GREATER_THAN", value: "GREATER_THAN" },
  { label: "GREATER_THAN_OR_EQUAL", value: "GREATER_THAN_OR_EQUAL" },
  { label: "LESS_THAN", value: "LESS_THAN" },
  { label: "LESS_THAN_OR_EQUAL", value: "LESS_THAN_OR_EQUAL" },
];

// Operator options by type
const OPERATOR_OPTIONS_BY_TYPE: Record<string, SelectOptionType[]> = {
  String: [
    { label: "EQUALS", value: "EQUALS" },
    { label: "NOT_EQUALS", value: "NOT_EQUALS" },
    { label: "BEGINS_WITH", value: "BEGINS_WITH" },
    { label: "ENDS_WITH", value: "ENDS_WITH" },
    { label: "CONTAINS", value: "CONTAINS" },
  ],
  Date: [
    { label: "EQUALS", value: "EQUALS" },
    { label: "BEFORE", value: "BEFORE" },
    { label: "AFTER", value: "AFTER" },
    { label: "BETWEEN", value: "BETWEEN" },
  ],
  Numeric: [
    { label: "EQUALS", value: "EQUALS" },
    { label: "NOT_EQUALS", value: "NOT_EQUALS" },
    { label: "GREATER_THAN", value: "GREATER_THAN" },
    { label: "GREATER_THAN_OR_EQUAL", value: "GREATER_THAN_OR_EQUAL" },
    { label: "LESS_THAN", value: "LESS_THAN" },
    { label: "LESS_THAN_OR_EQUAL", value: "LESS_THAN_OR_EQUAL" },
    { label: "BETWEEN", value: "BETWEEN" },
  ],
  List: [
    { label: "EQUALS", value: "EQUALS" },
    { label: "NOT_EQUALS", value: "NOT_EQUALS" }, // Optional, if supported
  ],
};

function getFieldType(
  fieldValue: string,
  optionsAdditionalParameters: any
): keyof typeof OPERATOR_OPTIONS_BY_TYPE | undefined {
  if (!fieldValue) return undefined;
  for (const type of Object.keys(optionsAdditionalParameters)) {
    if (
      optionsAdditionalParameters[type].some(
        (opt: SelectOptionType) => opt.value === fieldValue
      )
    ) {
      return type as keyof typeof OPERATOR_OPTIONS_BY_TYPE;
    }
  }
  return undefined;
}

type Cond = { field?: string; op: string; value: any };
type Group = { innerOps?: Array<"AND" | "OR">; nodes: Cond[]; field?: string };

interface AdditionalParameterFormProps {
  selectedFields: SelectOptionType[];
  control: Control<CampaignForm>;
  setValue: UseFormSetValue<CampaignForm>;
  indexChannel: number;
  onRemoveField?: (fieldValue: any) => void;
  onPayloadChange?: (payload: any) => void;
  editCampaignFields: any;
  isEdit: boolean;
  optionsAdditionalParameters: any;
}

const AdditionalParameterForm: React.FC<AdditionalParameterFormProps> = ({
  selectedFields,
  control,
  setValue,
  indexChannel,
  onRemoveField,
  onPayloadChange,
  editCampaignFields,
  isEdit,
  optionsAdditionalParameters,
}) => {
  const topPath =
    `campaign_channels[${indexChannel}].additional_parameters` as any;
  const watched = useWatch({ control: control as any, name: topPath }) as any;

  function parseAdditionalParams(payload: any): {
    groups: Group[];
    topOps: Array<"AND" | "OR">;
  } {
    // Top-level operators (between groups)
    let topOps: Array<"AND" | "OR"> = [];
    if (Array.isArray(payload?.ops)) {
      topOps = payload.ops;
    } else if (Array.isArray(payload?.op)) {
      topOps = payload.op;
    } else if (
      typeof payload?.op === "string" &&
      Array.isArray(payload?.nodes)
    ) {
      topOps = Array(Math.max(0, payload.nodes.length - 1)).fill(
        payload.op as "AND" | "OR"
      );
    }

    // Parse groups
    const groups: Group[] = Array.isArray(payload?.nodes)
      ? payload.nodes.map((g: any) => {
          let innerOps: Array<"AND" | "OR"> = [];
          if (Array.isArray(g.ops)) {
            innerOps = g.ops;
          } else if (Array.isArray(g.op)) {
            innerOps = g.op;
          } else if (typeof g.op === "string" && Array.isArray(g.nodes)) {
            innerOps = Array(Math.max(0, g.nodes.length - 1)).fill(
              g.op as "AND" | "OR"
            );
          }

          // If field at group level, use that; otherwise, try from first node (for edit shape)
          const field =
            g.field ?? (Array.isArray(g.nodes) && g.nodes[0]?.field);

          return {
            innerOps,
            nodes: g.nodes ?? [],
            field,
          } as Group;
        })
      : [];

    return { groups, topOps };
  }

  // local state mirrors the desired payload: { ops: [..], nodes: [ { op, nodes:[{field,op,value}] }, ... ] }
  const [topOps, setTopOps] = React.useState<Array<"AND" | "OR">>(() => {
    if (watched) {
      if (Array.isArray(watched.ops)) return watched.ops as Array<"AND" | "OR">;
      if (typeof watched.op === "string" && Array.isArray(watched.nodes))
        return Array(Math.max(0, watched.nodes.length - 1)).fill(
          watched.op as "AND" | "OR"
        );
    }
    return selectedFields.length > 1
      ? Array(Math.max(0, selectedFields.length - 1)).fill("AND")
      : [];
  });

  const [groups, setGroups] = React.useState<Group[]>(() => {
    if (watched && Array.isArray(watched.nodes)) {
      // migrate older shape (with op) to innerOps if needed
      return (watched.nodes as any).map((g: any) => {
        if (Array.isArray(g.ops)) {
          return { innerOps: g.ops, nodes: g.nodes, field: g.field } as Group;
        }
        if (typeof g.op === "string" && Array.isArray(g.nodes)) {
          return {
            innerOps: Array(Math.max(0, g.nodes.length - 1)).fill(g.op),
            nodes: g.nodes,
            field: g.field,
          } as Group;
        }
        return {
          innerOps: Array(Math.max(0, (g.nodes?.length ?? 1) - 1)).fill("AND"),
          nodes: g.nodes ?? [{ field: g.field, op: "", value: "" }],
          field: g.field,
        } as Group;
      });
    }
    return selectedFields.map((f) => ({
      innerOps: [],
      nodes: [{ field: f.value, op: "", value: "" }],
      field: f.value,
    }));
  });

  React.useEffect(() => {
    if (isEdit && editCampaignFields) {
      const { groups: parsedGroups, topOps: parsedTopOps } =
        parseAdditionalParams(editCampaignFields);
      setGroups(parsedGroups);
      setTopOps(parsedTopOps);
    }
  }, [isEdit, editCampaignFields]);

  // sync when selectedFields change: keep existing groups where field matches, add new ones
  React.useEffect(() => {
    setGroups((prev) => {
      const next: Group[] = selectedFields.map((f) => {
        const existing = prev.find((p) => p.field === f.value);
        if (existing) return existing;
        return {
          innerOps: [],
          nodes: [{ field: f.value, op: "", value: "" }],
          field: f.value,
        };
      });

      // adjust topOps to match new length (one less than groups.length)
      setTopOps((prevTopOps) => {
        const needed = Math.max(0, next.length - 1);
        const nextOps: Array<"AND" | "OR"> = Array.from(
          { length: needed },
          (_, i) => prevTopOps[i] ?? "AND"
        );
        return nextOps;
      });

      return next;
    });
  }, [selectedFields]);

  // whenever groups or topOps changes, push to form via setValue
  React.useEffect(() => {
    const nodesPayload = groups.map((g) => ({
      op:
        g.nodes.length > 1
          ? g.innerOps ?? Array(Math.max(0, g.nodes.length - 1)).fill("AND")
          : [],
      nodes: g.nodes,
    }));

    const payload = {
      op:
        groups.length > 1
          ? topOps ?? Array(Math.max(0, groups.length - 1)).fill("AND")
          : [],
      nodes: nodesPayload,
    };
    setValue(topPath, payload, { shouldValidate: false, shouldDirty: true });
    // also inform parent if provided
    try {
      onPayloadChange?.(payload);
    } catch (err) {
      // ignore
    }
  }, [groups, topOps, setValue, topPath]);

  // read validation errors from react-hook-form so we can show them in the local UI
  const { errors } = useFormState({ control: control as any }) as any;

  const getNodeError = (gIdx: number, nIdx: number, key: "op" | "value") => {
    try {
      return errors?.campaign_channels?.[indexChannel]?.additional_parameters
        ?.nodes?.[gIdx]?.nodes?.[nIdx]?.[key]?.message as string | undefined;
    } catch (e) {
      return undefined;
    }
  };

  const updateNode = (gidx: number, nidx: number, patch: Partial<Cond>) => {
    setGroups((prev) => {
      const copy = prev.map((g) => ({
        ...g,
        nodes: g.nodes.map((n) => ({ ...n })),
      }));
      copy[gidx].nodes[nidx] = { ...copy[gidx].nodes[nidx], ...patch };
      return copy;
    });
  };

  const addNode = (gidx: number) => {
    setGroups((prev) => {
      const copy = prev.map((g) => ({
        ...g,
        nodes: [...g.nodes],
        innerOps: g.innerOps ? [...g.innerOps] : [],
      }));
      copy[gidx].nodes.push({ field: prev[gidx].field, op: "", value: "" });
      // when adding a node, add a default innerOp for the adjacency
      copy[gidx].innerOps = copy[gidx].innerOps ?? [];
      copy[gidx].innerOps.push("AND");
      return copy;
    });
  };

  const removeNode = (gidx: number, nidx: number) => {
    setGroups((prev) => {
      const copy = prev.map((g) => ({
        ...g,
        nodes: [...g.nodes],
        innerOps: g.innerOps ? [...g.innerOps] : [],
      }));
      // remove the node
      copy[gidx].nodes.splice(nidx, 1);
      // remove the corresponding adjacency operator
      if (copy[gidx].innerOps && copy[gidx].innerOps.length > 0) {
        // if removing node at index nidx, remove innerOps[nidx] (operator after the node)
        // but if removing last node, remove the previous operator (nidx - 1)
        const removeIndex = nidx < copy[gidx].innerOps.length ? nidx : nidx - 1;
        if (removeIndex >= 0 && removeIndex < copy[gidx].innerOps.length) {
          copy[gidx].innerOps.splice(removeIndex, 1);
        }
      }

      if (copy[gidx].nodes.length === 0) {
        // if parent provided onRemoveField, remove the whole group
        if (onRemoveField) {
          const fieldToRemove = prev[gidx].field;
          // remove group locally
          const next = copy.filter((_, idx) => idx !== gidx);
          // inform parent to remove selected field
          try {
            onRemoveField(fieldToRemove);
          } catch (err) {
            // ignore
          }
          return next.length
            ? next
            : [
                {
                  op: "AND",
                  nodes: [{ field: undefined, op: "", value: "" }],
                  field: undefined,
                },
              ];
        }
        // otherwise ensure at least one empty node remains
        copy[gidx].nodes.push({ field: prev[gidx].field, op: "", value: "" });
      }
      return copy;
    });
  };

  return (
    <div>
      {groups.map((g, gi) => (
        <div key={g.field ?? gi} style={{ marginBottom: 12 }}>
          {/* group container with background and padding */}
          <div
            style={{
              backgroundColor: "#e8e9f9",
              padding: 12,
              borderRadius: 8,
            }}
          >
            <div className="additional-parameter-heading">
              {selectedFields[gi]?.label || g.field}
            </div>

            {g.nodes.map((n, ni) => {
              const fieldType = getFieldType(
                n.field ?? "",
                optionsAdditionalParameters
              );
              const operatorOptions =
                (fieldType && OPERATOR_OPTIONS_BY_TYPE[fieldType]) ||
                OPERATOR_OPTIONS;
              return (
                <div key={ni} style={{ marginTop: 8 }}>
                  <Flex style={{ marginBottom: 8 }}>
                    <OperatorSelect
                      required={true}
                      value={n.op}
                      onChange={(val) => {
                        // when operator changes, normalize the stored value for the UI
                        const newValue = (() => {
                          if (val === "BETWEEN") {
                            return ["", ""]; // will be filled by datepickers or numeric inputs
                          }
                          if (val === "BEFORE" || val === "AFTER") {
                            return "";
                          }
                          // default for other ops: keep existing single value (string)
                          return Array.isArray(n.value)
                            ? n.value[0] ?? ""
                            : n.value ?? "";
                        })();

                        updateNode(gi, ni, { op: val, value: newValue });
                      }}
                      options={operatorOptions}
                      placeholder="Select Operator"
                      disabled={false}
                    />

                    {/* render date pickers when operator implies dates */}
                    {(() => {
                      if (n.op === "BEFORE" || n.op === "AFTER") {
                        return (
                          <SimpleDatePicker
                            required={true}
                            value={n.value ?? ""} // n.value holds an ISO string or ""
                            onChange={(val) => {
                              const iso = val
                                ? typeof val === "object" &&
                                  (val as any) instanceof Date
                                  ? (val as Date).toISOString().split("T")[0]
                                  : String(val)
                                : "";
                              updateNode(gi, ni, { value: iso });
                            }}
                            placeholder="Select date"
                          />
                        );
                      }

                      if (n.op === "EQUALS" && fieldType === "Date") {
                        return (
                          <SimpleDatePicker
                            required={true}
                            value={n.value ?? ""}
                            onChange={(val) => {
                              const iso = val
                                ? typeof val === "object" &&
                                  (val as any) instanceof Date
                                  ? (val as Date).toISOString().split("T")[0]
                                  : String(val)
                                : "";
                              updateNode(gi, ni, { value: iso });
                            }}
                            placeholder="Select date"
                          />
                        );
                      }
                      // BETWEEN handling
                      if (n.op === "BETWEEN") {
                        const fieldType = n.field
                          ? getFieldType(n.field, optionsAdditionalParameters)
                          : undefined;
                        if (fieldType === "Date") {
                          // Render two date pickers for date BETWEEN
                          return (
                            <>
                              <SimpleDatePicker
                                required={true}
                                value={
                                  Array.isArray(n.value) && n.value[0]
                                    ? n.value[0]
                                    : ""
                                }
                                onChange={(val) => {
                                  const iso = val
                                    ? typeof val === "object" &&
                                      (val as any) instanceof Date
                                      ? (val as Date)
                                          .toISOString()
                                          .split("T")[0]
                                      : String(val)
                                    : "";
                                  updateNode(gi, ni, {
                                    value: [
                                      iso,
                                      Array.isArray(n.value) && n.value[1]
                                        ? n.value[1]
                                        : "",
                                    ],
                                  });
                                }}
                                placeholder="From Date"
                              />
                              <SimpleDatePicker
                                required={true}
                                value={
                                  Array.isArray(n.value) && n.value[1]
                                    ? n.value[1]
                                    : ""
                                }
                                onChange={(val) => {
                                  const iso = val
                                    ? typeof val === "object" &&
                                      (val as any) instanceof Date
                                      ? (val as Date)
                                          .toISOString()
                                          .split("T")[0]
                                      : String(val)
                                    : "";
                                  updateNode(gi, ni, {
                                    value: [
                                      Array.isArray(n.value) && n.value[0]
                                        ? n.value[0]
                                        : "",
                                      iso,
                                    ],
                                  });
                                }}
                                placeholder="To Date"
                              />
                            </>
                          );
                        } else {
                          // Numeric BETWEEN
                          return (
                            <>
                              <SimpleInput
                                required={true}
                                value={
                                  Array.isArray(n.value) && n.value[0] != null
                                    ? String(n.value[0])
                                    : ""
                                }
                                onChange={(v) =>
                                  updateNode(gi, ni, {
                                    value: [
                                      v === "" ? null : Number(v),
                                      Array.isArray(n.value) &&
                                      n.value[1] != null
                                        ? n.value[1]
                                        : null,
                                    ],
                                  })
                                }
                                placeholder="From"
                              />
                              <SimpleInput
                                required={true}
                                value={
                                  Array.isArray(n.value) && n.value[1] != null
                                    ? String(n.value[1])
                                    : ""
                                }
                                onChange={(v) =>
                                  updateNode(gi, ni, {
                                    value: [
                                      Array.isArray(n.value) &&
                                      n.value[0] != null
                                        ? n.value[0]
                                        : null,
                                      v === "" ? null : Number(v),
                                    ],
                                  })
                                }
                                placeholder="To"
                              />
                            </>
                          );
                        }
                      }

                      // default single value input
                      return (
                        <SimpleInput
                          required={true}
                          value={
                            Array.isArray(n.value)
                              ? n.value[0] != null
                                ? String(n.value[0])
                                : ""
                              : n.value != null
                              ? String(n.value)
                              : ""
                          }
                          onChange={(v) => updateNode(gi, ni, { value: v })}
                          placeholder="Value"
                        />
                      );
                    })()}

                    <Button
                      iconOnly
                      variant="outline"
                      className="button-add"
                      icon={<IconAddStyle />}
                      onClick={() => addNode(gi)}
                    />
                    <Button
                      iconOnly
                      variant="outline"
                      className="button-delete"
                      icon={<IconDelete />}
                      onClick={() => removeNode(gi, ni)}
                      disabled={!onRemoveField && g.nodes.length === 1}
                    />
                  </Flex>

                  {/* inline error messages for operator/value (from Yup via resolver) */}
                  {(() => {
                    const opErr = getNodeError(gi, ni, "op");
                    const valErr = getNodeError(gi, ni, "value");
                    if (!opErr && !valErr) return null;
                    return (
                      <div style={{ marginTop: 4 }}>
                        {opErr && (
                          <div style={{ color: "#d32f2f", fontSize: 12 }}>
                            {opErr}
                          </div>
                        )}
                        {valErr && (
                          <div style={{ color: "#d32f2f", fontSize: 12 }}>
                            {valErr}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* show group operator between each pair of rows */}
                  {ni < g.nodes.length - 1 && (
                    <Flex style={{ marginBottom: 8 }}>
                      <Button
                        label="AND"
                        variant={
                          g.innerOps?.[ni] === "AND" ? "block" : "outline"
                        }
                        onClick={() =>
                          setGroups((prev) =>
                            prev.map((grp, idx) => {
                              if (idx !== gi) return grp;
                              const newInner = (grp.innerOps ?? []).slice();
                              newInner[ni] = "AND";
                              return { ...grp, innerOps: newInner };
                            })
                          )
                        }
                      />
                      <Button
                        label="OR"
                        variant={
                          g.innerOps?.[ni] === "OR" ? "block" : "outline"
                        }
                        className="ml-2"
                        onClick={() =>
                          setGroups((prev) =>
                            prev.map((grp, idx) => {
                              if (idx !== gi) return grp;
                              const newInner = (grp.innerOps ?? []).slice();
                              newInner[ni] = "OR";
                              return { ...grp, innerOps: newInner };
                            })
                          )
                        }
                      />
                    </Flex>
                  )}
                </div>
              );
            })}
          </div>

          {/* top-level operator between groups (centered between group containers) */}
          {gi < groups.length - 1 && (
            <Flex style={{ justifyContent: "center", margin: "8px 0" }}>
              <Button
                label="AND"
                variant={topOps[gi] === "AND" ? "block" : "outline"}
                onClick={() =>
                  setTopOps((prev) => {
                    const c = [...prev];
                    c[gi] = "AND";
                    return c;
                  })
                }
              />
              <Button
                label="OR"
                variant={topOps[gi] === "OR" ? "block" : "outline"}
                className="ml-2"
                onClick={() =>
                  setTopOps((prev) => {
                    const c = [...prev];
                    c[gi] = "OR";
                    return c;
                  })
                }
              />
            </Flex>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdditionalParameterForm;
